import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { isPushSupported, ensurePushSubscribed, enablePushNotifications } from "../utils/pushNotifications";

const PUSH_PROMPT_DISMISSED_KEY = 'pushPromptDismissed';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [showPushPrompt, setShowPushPrompt] = useState(false);
  const [pushPromptBusy, setPushPromptBusy] = useState(false);
  const prevCountRef = useRef(0);
  const location = useLocation();
  
  // Check for stored auth token on component mount
  useEffect(() => {
    const storedToken = localStorage.getItem('authToken');
    if (storedToken) {
      try {
        const tokenPayload = JSON.parse(atob(storedToken.split('.')[1]));
        const username = tokenPayload['cognito:username'] || tokenPayload.username || 'User';
        setUsername(username);
        setIsLoggedIn(true);
      } catch (e) {
        console.error('Error parsing stored token:', e);
        localStorage.removeItem('authToken');
      }
    }
  }, []);
  
  // Close menu when clicking outside
  useEffect(() => {
    const closeMenu = (e) => {
      // Don't close if clicking inside the nav-links area
      if (!e.target.closest('.nav-links') && !e.target.closest('.hamburger')) {
        setIsMenuOpen(false);
      }
    };
    
    if (isMenuOpen) {
      document.addEventListener('click', closeMenu);
    }
    
    return () => {
      document.removeEventListener('click', closeMenu);
    };
  }, [isMenuOpen]);

  // Prevent body scrolling when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      // Prevent body scroll and save current scroll position
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflowY = 'hidden';
    } else {
      // Restore body scroll and position
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflowY = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflowY = '';
    };
  }, [isMenuOpen]);

  // Poll for unread message count
  useEffect(() => {
    if (!isLoggedIn) {
      setUnreadCount(0);
      return;
    }

    const fetchUnreadCount = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) return;

        const response = await fetch('https://api.omegavoid.com/omega-links/unread-count', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          const newCount = data.total_unread || 0;

          // Browser notification if count increased
          if (newCount > prevCountRef.current && prevCountRef.current >= 0) {
            if ('Notification' in window && Notification.permission === 'granted') {
              new Notification('Omega Inbox', {
                body: `You have ${newCount} new message${newCount > 1 ? 's' : ''}`,
                icon: '/logo192.png'
              });
            }
          }

          prevCountRef.current = newCount;
          setUnreadCount(newCount);
        }
      } catch (error) {
        console.debug('Notification poll failed:', error);
      }
    };

    fetchUnreadCount();
    const intervalId = setInterval(fetchUnreadCount, 45000);
    return () => clearInterval(intervalId);
  }, [isLoggedIn]);

  // Push A + C: on login, either silently re-subscribe (already granted)
  // OR show a one-time modal asking to enable (permission still 'default').
  useEffect(() => {
    if (!isLoggedIn || !isPushSupported()) return;
    const token = localStorage.getItem('authToken');
    if (!token) return;

    if (Notification.permission === 'granted') {
      // C: user already said yes at some point → resubscribe silently
      ensurePushSubscribed(token);
      return;
    }
    if (Notification.permission === 'default' && !localStorage.getItem(PUSH_PROMPT_DISMISSED_KEY)) {
      // A: never asked + not previously dismissed → show one-time modal
      const t = setTimeout(() => setShowPushPrompt(true), 800);
      return () => clearTimeout(t);
    }
  }, [isLoggedIn]);

  const handleEnablePushPrompt = async () => {
    try {
      setPushPromptBusy(true);
      const token = localStorage.getItem('authToken');
      await enablePushNotifications(token);
      localStorage.setItem(PUSH_PROMPT_DISMISSED_KEY, String(Date.now()));
      setShowPushPrompt(false);
    } catch (e) {
      console.warn('Enable from prompt failed:', e && e.message);
      // Whatever happened (denied, error), don't keep nagging.
      localStorage.setItem(PUSH_PROMPT_DISMISSED_KEY, String(Date.now()));
      setShowPushPrompt(false);
    } finally {
      setPushPromptBusy(false);
    }
  };

  const handleDismissPushPrompt = () => {
    localStorage.setItem(PUSH_PROMPT_DISMISSED_KEY, String(Date.now()));
    setShowPushPrompt(false);
  };

  // Close menu when route changes (for better UX)
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const toggleMenu = (e) => {
    e.stopPropagation(); // Prevent event bubbling
    setIsMenuOpen(!isMenuOpen);
  };
  
  const handleLinkClick = () => {
    setIsMenuOpen(false);
  };
  
  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userTokens');
    localStorage.removeItem('tempUserId');
    localStorage.removeItem('isPremiumUser');
    localStorage.removeItem('hiddenMessages');
    
    setIsLoggedIn(false);
    setUsername('');
    setUnreadCount(0);
    setIsMenuOpen(false); // Close menu on logout
    // Force page reload to refresh all components
    window.location.reload();
  };

  return (
    <nav>
      <div 
        className={`hamburger ${isMenuOpen ? 'active' : ''}`} 
        onClick={toggleMenu}
        aria-label="Toggle navigation menu"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleMenu(e);
          }
        }}
      >
        <span></span>
        <span></span>
        <span></span>
      </div>
      
      <div 
        className={`nav-links ${isMenuOpen ? 'active' : ''}`}
        onClick={(e) => e.stopPropagation()} // Prevent menu from closing when clicking inside
      >
        <Link
          to="/"
          className={location.pathname === '/' ? 'active' : ''}
          onClick={handleLinkClick}
        >
          Home
        </Link>
        <Link
          to="/map"
          className={location.pathname === '/map' ? 'active' : ''}
          onClick={handleLinkClick}
        >
          Omega Void
        </Link>
        <Link
          to="/map-premium"
          className={location.pathname === '/map-premium' ? 'active' : ''}
          onClick={handleLinkClick}
        >
          Personal Void
        </Link>
        <Link
          to="/omega-inbox"
          className={location.pathname === '/omega-inbox' ? 'active' : ''}
          onClick={handleLinkClick}
          style={{ position: 'relative' }}
        >
          Omega Inbox
          {unreadCount > 0 && (
            <span className="navbar-unread-badge">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Link>
        <Link
          to="/howto"
          className={location.pathname === '/howto' ? 'active' : ''}
          onClick={handleLinkClick}
        >
          How-To Use
        </Link>
      </div>
      
      {/* Login/User info section - stays in same place for both desktop and mobile */}
      <div className="nav-auth">
        {isLoggedIn ? (
          <div className="user-profile">
            <span className="username" style={{ color: "#fff" }}>
              {username.length > 7
                ? `${username.slice(0, 5)}${'*'.repeat(username.length - 7)}${username.slice(-2)}`
                : username}
            </span>
            <button className="logout-button" style={{ color: "yellow" }} onClick={handleLogout}>
              Logout
            </button>
          </div>
        ) : (
          <Link to="/login" className="login-button">Login</Link>
        )}
      </div>

      {showPushPrompt && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.65)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
          onClick={handleDismissPushPrompt}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#1a1a1a',
              color: '#fff',
              border: '1px solid #FFD500',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '380px',
              width: '90%',
              boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
              textAlign: 'center'
            }}
          >
            <div style={{ fontSize: '38px', marginBottom: '8px' }}>🔔</div>
            <h3 style={{ margin: '0 0 10px 0', color: '#FFD500' }}>Turn on notifications?</h3>
            <p style={{ margin: '0 0 6px 0', fontSize: '14px', lineHeight: 1.5 }}>
              Get pinged when a friend sends you an Omega Link — even when the tab is closed.
            </p>
            <p style={{ margin: '0 0 18px 0', fontSize: '12px', color: '#aaa' }}>
              Notifications never contain the sender or message content.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button
                onClick={handleDismissPushPrompt}
                disabled={pushPromptBusy}
                style={{
                  padding: '10px 18px',
                  background: 'transparent',
                  color: '#ccc',
                  border: '1px solid #555',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Not now
              </button>
              <button
                onClick={handleEnablePushPrompt}
                disabled={pushPromptBusy}
                style={{
                  padding: '10px 18px',
                  background: '#FFD500',
                  color: '#000',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 600
                }}
              >
                {pushPromptBusy ? '...' : 'Enable'}
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;